/**
 * ReceiptStatePipeline · ARC-015 pattern · Sprint 9.3.
 *
 * The second implementation of the Daily-Pricing lifecycle pattern —
 * this time for Receipt Reconciliation. Presentation only; every
 * transition is invoked via the existing endpoints:
 *
 *   GET  /api/receiving/{id}/lifecycle
 *   POST /api/receiving/{id}/advance   {target_state, reason?, force?}
 *   POST /api/receiving/{id}/revert    {target_state, reason}
 */
import { useCallback, useEffect, useState } from "react";
import {
  CheckCircle, Circle, Clock, Prohibit, Warning, ArrowRight, ArrowClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CANCELLED  = "cancelled";
const EXCEPTION  = "exception";

function fmtDT(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function ReceiptStatePipeline({ invoiceId, confidence, onTransitioned }) {
  const [state, setState] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/receiving/${invoiceId}/lifecycle`);
      setState(data);
    } catch {
      /* silent — parent handles overall errors */
    }
  }, [invoiceId]);

  useEffect(() => { if (invoiceId) load(); }, [invoiceId, load]);

  if (!state) return null;

  const { business_state: current, happy_path, legal_actions, status_history } = state;
  const currentIdx = happy_path.findIndex((s) => s.state === current);
  const offPath    = current === CANCELLED || current === EXCEPTION;

  const advance = async (target, opts = {}) => {
    if (opts.needsReason && !reason.trim()) {
      toast.error("A reason is required for this transition.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/receiving/${invoiceId}/advance`, {
        target_state: target,
        reason: reason.trim() || null,
        force: !!opts.force,
      });
      toast.success(`Advanced to ${labelOf(target, happy_path)}.`);
      setReason("");
      await load();
      onTransitioned?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message || "Transition failed.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  // Waiting-for label: the first illegal transition's guard reason
  const nextHappy = happy_path[currentIdx + 1];
  const waitingActionForHappy = legal_actions?.find(
    (a) => a.to === nextHappy?.state && !a.guard_ok,
  );

  return (
    <section
      data-testid="receipt-state-pipeline"
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-4"
    >
      <header className="flex items-baseline gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-slate-800">
          Receipt Reconciliation Lifecycle
        </h3>
        <span className="text-[11px] text-slate-500 uppercase tracking-wider">ARC-015</span>
        <span
          data-testid="receipt-pipeline-current-state"
          className={[
            "ml-auto inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold",
            current === "landed_cost_complete" || current === "archived"
              ? "border-emerald-300 bg-emerald-50 text-emerald-800"
              : current === CANCELLED
                ? "border-rose-300 bg-rose-50 text-rose-800"
                : current === EXCEPTION
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-sky-300 bg-sky-50 text-sky-800",
          ].join(" ")}
        >
          {state.label}
        </span>
      </header>

      {/* Off-path banners */}
      {current === CANCELLED && (
        <div data-testid="receipt-pipeline-cancelled" className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900 flex items-center gap-2">
          <Prohibit size={16} weight="fill" /> This receipt was cancelled. Off happy-path.
        </div>
      )}
      {current === EXCEPTION && (
        <div data-testid="receipt-pipeline-exception" className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-center gap-2">
          <Warning size={16} weight="fill" /> Receipt is in the exception queue — operator action required.
        </div>
      )}

      {/* Stepper */}
      {!offPath && (
        <ol className="flex items-center flex-wrap gap-y-2" data-testid="receipt-pipeline-steps">
          {happy_path.map((s, i) => {
            const done = i < currentIdx;
            const active = i === currentIdx;
            return (
              <li key={s.state} className="flex items-center gap-1.5">
                <span
                  data-testid={`receipt-pipeline-step-${s.state}`}
                  className={[
                    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
                    done
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                      : active
                        ? "border-sky-500 bg-sky-100 text-sky-900 font-semibold"
                        : "border-slate-200 bg-white text-slate-500",
                  ].join(" ")}
                >
                  {done
                    ? <CheckCircle size={11} weight="fill" />
                    : active
                      ? <Clock size={11} weight="fill" />
                      : <Circle size={10} weight="regular" />}
                  {s.label}
                </span>
                {i < happy_path.length - 1 && (
                  <span className="text-slate-300" aria-hidden>→</span>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {/* Waiting-for + Confidence quick facts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <FactCard label="Waiting for" testid="receipt-pipeline-waiting-for">
          {waitingActionForHappy?.waiting_for
            ? waitingActionForHappy.waiting_for
            : state.is_terminal
              ? "Nothing — receipt is in a terminal state."
              : "Ready for the next transition."}
        </FactCard>
        <FactCard label="Current confidence" testid="receipt-pipeline-current-confidence">
          {confidence?.overall_confidence != null
            ? `${Math.round(confidence.overall_confidence * 100)}% · ${
                confidence.high_confidence ? "High confidence" : "Manual review recommended"
              }`
            : "Not scored yet."}
        </FactCard>
      </div>

      {/* Next legal actions */}
      {legal_actions?.length > 0 && !state.is_terminal && (
        <div data-testid="receipt-pipeline-actions">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Next legal actions
          </div>
          <div className="flex flex-wrap gap-2">
            {legal_actions.map((a) => (
              <ActionButton
                key={a.to}
                action={a}
                busy={busy}
                reason={reason}
                onReasonChange={setReason}
                onGo={() => advance(a.to, { needsReason: a.reason_required })}
                onForce={() => advance(a.to, { needsReason: true, force: true })}
              />
            ))}
          </div>
          {legal_actions.some((a) => a.reason_required) && (
            <div className="mt-2">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Reason (required for cancel · exception · revert · force)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="receipt-pipeline-reason"
                placeholder="e.g. Damaged goods returned to Suncor"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
          )}
        </div>
      )}

      {/* History */}
      {status_history?.length > 0 && (
        <details className="mt-2" data-testid="receipt-pipeline-history">
          <summary className="cursor-pointer text-xs font-semibold text-slate-700 hover:text-slate-900">
            Status history ({status_history.length} transition{status_history.length === 1 ? "" : "s"})
          </summary>
          <ol className="mt-2 space-y-1 text-[12px]">
            {status_history.map((h, i) => (
              <li key={i} data-testid={`receipt-pipeline-history-${i}`}
                  className="flex items-baseline gap-2 border-l-2 border-slate-200 pl-2">
                <span className="text-slate-500 tabular-nums whitespace-nowrap">{fmtDT(h.at)}</span>
                <span className="text-slate-800">
                  <b>{labelForState(h.from, happy_path)}</b> → <b>{labelForState(h.to, happy_path)}</b>
                </span>
                <span className="text-slate-500">· {h.by || "system"}</span>
                {h.reason && <span className="text-slate-500 italic">— "{h.reason}"</span>}
                {h.forced && <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">forced</span>}
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}

function ActionButton({ action, busy, reason, onGo, onForce }) {
  const disabled = busy || (!action.guard_ok && !action.reason_required);
  const label    = action.to_label;
  const tone     = actionTone(action.to);
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onGo}
        disabled={disabled}
        data-testid={`receipt-pipeline-advance-${action.to}`}
        className={[
          "inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold border transition-colors",
          tone.base,
          disabled ? "opacity-50 cursor-not-allowed" : tone.hover,
        ].join(" ")}
        title={!action.guard_ok ? `Waiting for: ${action.waiting_for}` : ""}
      >
        {action.to === CANCELLED ? <Prohibit size={12} weight="fill" />
         : action.to === EXCEPTION ? <Warning size={12} weight="fill" />
         : <ArrowRight size={12} weight="bold" />}
        {label}
      </button>
      {!action.guard_ok && (
        <button
          type="button"
          onClick={onForce}
          disabled={busy || !reason.trim()}
          data-testid={`receipt-pipeline-force-${action.to}`}
          title={action.waiting_for || ""}
          className="inline-flex items-center rounded-md border border-slate-300 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowClockwise size={11} weight="bold" className="mr-1" />
          Force
        </button>
      )}
    </div>
  );
}

function FactCard({ label, testid, children }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
      <div className="text-slate-800">{children}</div>
    </div>
  );
}

function actionTone(target) {
  if (target === CANCELLED) return { base: "border-rose-200 bg-rose-50 text-rose-700", hover: "hover:bg-rose-100" };
  if (target === EXCEPTION) return { base: "border-amber-200 bg-amber-50 text-amber-800", hover: "hover:bg-amber-100" };
  return { base: "border-emerald-300 bg-emerald-50 text-emerald-800", hover: "hover:bg-emerald-100" };
}

function labelOf(target, happyPath) {
  return happyPath.find((s) => s.state === target)?.label ?? target;
}
function labelForState(state, happyPath) {
  if (!state) return "—";
  const found = happyPath.find((s) => s.state === state);
  if (found) return found.label;
  const overrides = { cancelled: "Cancelled", exception: "Exception" };
  return overrides[state] || state;
}
